import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Layout, Zap, ArrowLeft } from "lucide-react";

const features = [
  {
    icon: Zap,
    accentClass: "bg-orange-50",
    iconStyle: { color: "var(--orange)" },
    title: "Instant Renders",
    desc: "Get high-quality AI-generated room visualizations in under 60 seconds.",
  },
  {
    icon: Layout,
    accentClass: "bg-orange-50",
    iconStyle: { color: "var(--orange)" },
    title: "Auto-Furnish",
    desc: "Let our AI automatically select and place furniture based on your room's architecture.",
  },
  {
    icon: Sparkles,
    accentClass: "bg-orange-50",
    iconStyle: { color: "var(--orange)" },
    title: "Custom Styles",
    desc: "Choose from Minimal, Modern, Luxury, and Bohemian design styles.",
  },
];

export default function Home() {
  return (
    <div className="min-h-screen" style={{ background: "var(--surface)" }}>
      {/* Hero */}
      <section className="gradient-bg-hero py-20 sm:py-28 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto text-center animate-fade-up">
          <div
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium mb-5"
            style={{ background: "var(--orange-pale)", color: "var(--orange)", border: "1px solid var(--orange-glow)" }}
          >
            <Sparkles className="w-3 h-3" />
            AI-Powered Interior Design
          </div>

          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-5"
            style={{ fontFamily: "'Outfit', sans-serif", color: "var(--text-primary)" }}
          >
            Transform Your Space{" "}
            <span className="gradient-text">With Intelligence</span>
          </h1>

          <p className="text-base md:text-lg mb-8 max-w-xl mx-auto leading-relaxed" style={{ color: "var(--text-secondary)" }}>
            Upload an empty room and watch as our AI creates a fully furnished masterpiece in seconds.
            Choose your style, define your mood.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link to="/interior-design-ai/dashboard">
              <button
                className="btn-primary-glow inline-flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold text-white transition-all"
                style={{ background: "linear-gradient(135deg, var(--orange), var(--orange-light))" }}
              >
                Get Started <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
            <Link to="/interior-design-ai/dashboard">
              <button
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold transition-all"
                style={{ background: "var(--surface)", border: "1px solid var(--border-strong)", color: "var(--text-primary)" }}
              >
                View Dashboard
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
        <h2
          className="text-2xl sm:text-3xl font-bold text-center mb-12"
          style={{ fontFamily: "'Outfit', sans-serif", color: "var(--text-primary)" }}
        >
          How InteriorAI Works
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {features.map(({ icon: Icon, accentClass, iconStyle, title, desc }, i) => (
            <div key={i} className="card-premium p-6 text-center animate-fade-up" style={{ animationDelay: `${i * 0.1}s` }}>
              <div className={`w-14 h-14 rounded-2xl ${accentClass} flex items-center justify-center mx-auto mb-4`}>
                <Icon className="w-6 h-6" style={iconStyle} />
              </div>
              <h3 className="font-semibold mb-2" style={{ fontFamily: "'Outfit', sans-serif", color: "var(--text-primary)" }}>
                {title}
              </h3>
              <p className="text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6" style={{ borderTop: "1px solid var(--border)", background: "var(--surface-soft)" }}>
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ background: "linear-gradient(135deg, var(--orange), var(--orange-light))" }}>
              <Sparkles className="w-3 h-3 text-white" />
            </div>
            <span className="font-bold" style={{ color: "var(--text-primary)" }}>InteriorAI</span>
          </div>
          <p style={{ color: "var(--text-muted)" }}>AI-powered interior design. Results are generated for demonstration.</p>
          <Link
            to="/marketplace"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-all"
            style={{ background: "var(--surface)", border: "1px solid var(--border-strong)", color: "var(--text-primary)" }}
          >
            <ArrowLeft className="w-4 h-4" /> Return to Marketplace
          </Link>
        </div>
      </footer>
    </div>
  );
}
